import React, { Fragment, useCallback, useState, useEffect } from "react";

import { get } from "lodash";
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import tvgConf from "@tvg/conf";

import ReviewPageRegistration from "@tvg/atomic-ui/_templates/ReviewPageRegistration";
import { REGISTRATION_STEPS } from "@tvg/atomic-ui/_static/Constants/progressBar";
import { replaceAll } from "@tvg/utils/generalUtils";
import {
  onBackButtonClickGtmEvent,
  onEditClickGtmEvent
} from "../utils/gtm/otherInteractions";

import {
  handleBackButtonHeader,
  handleSubmitRegistrationButton,
  handleLegalContentButton,
  setLegalContentOrigin
} from "../utils/handlers";
import { onSelectProviderGtmEvent } from "../utils/gtm/tvgProvider";

import {
  getAddressFields,
  getCurrentStep,
  getIsEditing,
  getOnfidoToggle,
  getPerimeterXToggle,
  getRegistrationAllPromosWithTC,
  getRegistrationTexts,
  getRobotMessageToggle,
  getSelectedPromo,
  getSsnFields,
  getStoredSelectedProvider,
  getWelcomeFields
} from "../utils/selectors";
import {
  setCurrentStepRegistrationAction,
  setIsEditingRegistrationAction,
  handlePromoCodeModalRegistrationAction,
  handleTvPreferencesModalRegistrationAction
} from "../actions";
import { onEnterPromoCodeGtmEvent } from "../utils/gtm/promoCode";
import { onVirtualPageViewGtmEvent } from "../utils/gtm/pageViews";

export const handleEditPersonalDetails =
  ({ dispatch }) =>
  () => {
    dispatch(setIsEditingRegistrationAction(true));
    dispatch(setCurrentStepRegistrationAction(REGISTRATION_STEPS.WELCOME));
  };

export const handleEditEmail =
  ({ dispatch }) =>
  () => {
    dispatch(setIsEditingRegistrationAction(true));
    dispatch(setCurrentStepRegistrationAction(REGISTRATION_STEPS.WELCOME));
  };

export const handleEditAddressDetails =
  ({ dispatch }) =>
  () => {
    dispatch(setIsEditingRegistrationAction(true));
    dispatch(setCurrentStepRegistrationAction(REGISTRATION_STEPS.ADDRESS));
  };

export const handleEditLast4SSN =
  ({ dispatch }) =>
  () => {
    dispatch(setIsEditingRegistrationAction(true));
    dispatch(setCurrentStepRegistrationAction(REGISTRATION_STEPS.SSN));
  };

export const handleEditEnterPromo =
  ({ dispatch }) =>
  () => {
    onEnterPromoCodeGtmEvent("Review Your Information");
    dispatch(handlePromoCodeModalRegistrationAction(true));
  };

export const handleEditHasPromo =
  ({ dispatch }) =>
  () => {
    dispatch(handlePromoCodeModalRegistrationAction(true));
  };

export const handleEditTVProvider =
  ({ dispatch }) =>
  () => {
    dispatch(handleTvPreferencesModalRegistrationAction(true));
  };

export const buildBodyReview =
  ({
    dispatch,
    welcomeFields,
    addressFields,
    ssnFields,
    isPasswordVisible,
    setIsPasswordVisible,
    hasPromo,
    hasRaf,
    selectedPromo,
    selectedTvProvider,
    registrationAllPromosWithTC
  }) =>
  () => {
    const array = [
      {
        hasBorderBottom: true,
        hasBorderTop: true,
        wrapperMarginBottom: 22,
        rows: [
          {
            title: "Name",
            hasEdit: true,
            handleEdit: () => {
              onEditClickGtmEvent(
                "Personal Details",
                "Review Your Information"
              );
              handleEditPersonalDetails({ dispatch })();
            },
            qaLabel: "personal",
            marginBottom: 8
          },
          {
            text: `${get(welcomeFields, "firstName", "")} ${get(
              welcomeFields,
              "lastName",
              ""
            )}`,
            qaLabel: "name-text",
            marginBottom: 16
          },
          {
            title: "Date of Birth",
            qaLabel: "birth-title",
            marginBottom: 8
          },
          {
            text: replaceAll(get(welcomeFields, "birth", ""), " ", "/"),
            qaLabel: "birth-text",
            marginBottom: 12
          }
        ]
      },
      {
        hasBorderBottom: true,
        wrapperMarginBottom: 22,
        rows: [
          {
            title: "Email",
            hasEdit: true,
            handleEdit: () => {
              onEditClickGtmEvent("Email", "Review Your Information");
              handleEditEmail({ dispatch })();
            },
            qaLabel: "email-title",
            marginBottom: 8
          },
          {
            text: welcomeFields.email,
            qaLabel: "email-text",
            marginBottom: 16
          },
          {
            title: "Phone Number",
            qaLabel: "phone-title",
            marginBottom: 8
          },
          {
            text: welcomeFields.phone,
            qaLabel: "phone-text",
            marginBottom: 12
          }
        ]
      },
      {
        hasBorderBottom: true,
        wrapperMarginBottom: 22,
        rows: [
          {
            title: "Address Details",
            hasEdit: true,
            handleEdit: () => {
              onEditClickGtmEvent("Address Details", "Review Your Information");
              handleEditAddressDetails({ dispatch })();
            },
            qaLabel: "address-title",
            marginBottom: 8
          },
          {
            text: addressFields.home.street,
            qaLabel: "address-street",
            marginBottom: 4
          },
          {
            text: `Apt/Ste ${addressFields.home.details}`,
            qaLabel: "address-details",
            marginBottom: 4
          },
          {
            text: `${addressFields.home.city}, ${addressFields.home.state} ${addressFields.home.zip}`,
            qaLabel: "address-state",
            marginBottom: 12
          }
        ]
      },
      {
        hasBorderBottom: true,
        wrapperMarginBottom: 16,
        rows: [
          {
            title: "Last 4 SSN",
            hasEdit: true,
            handleEdit: () => {
              onEditClickGtmEvent("Last 4 SSN", "Review Your Information");
              handleEditLast4SSN({ dispatch })();
            },
            qaLabel: "ssn-title",
            marginBottom: 8
          },
          {
            text: ssnFields.ssn.join(""),
            isPassword: true,
            isPasswordVisible,
            handleShowPassword: () => {
              setIsPasswordVisible(!isPasswordVisible);
            },
            qaLabel: "ssn-text",
            marginBottom: 12
          }
        ]
      },
      {
        hasBorderBottom: true,
        wrapperMarginBottom: 16,
        rows: [
          {
            title: "TV Provider",
            isOptional: true,
            hasEdit: true,
            customEditText: get(selectedTvProvider, "key")
              ? "Change Provider"
              : "Select provider",
            handleEdit: () => {
              get(selectedTvProvider, "key")
                ? onEditClickGtmEvent("TV Provider", "Review Your Information")
                : onSelectProviderGtmEvent("TVProvider");
              handleEditTVProvider({ dispatch })();
            },
            qaLabel: "cable",
            marginBottom: get(selectedTvProvider, "key") ? 8 : 16
          },
          {
            text: get(selectedTvProvider, "key"),
            qaLabel: "selected-tv-provider",
            marginBottom: 12
          }
        ]
      }
    ];

    if (!hasPromo && !hasRaf) {
      array.push({
        hasBorderBottom: false,
        rows: [
          {
            customEditText: "Enter Promo Code",
            hasEdit: true,
            handleEdit: handleEditEnterPromo({ dispatch }),
            qaLabel: "enter-promo",
            marginBottom: 12
          }
        ]
      });
    } else if (hasPromo) {
      array.push({
        hasBorderBottom: false,
        wrapperOnClick: registrationAllPromosWithTC
          ? () => {
              onEditClickGtmEvent("Promo", "Review Your Information");
              handleEditHasPromo({ dispatch })();
            }
          : () => {},
        rows: [
          {
            title: "Promo",
            hasEdit: true,
            handleEdit: () => {
              onEditClickGtmEvent("Promo", "Review Your Information");
              handleEditHasPromo({ dispatch })();
            },
            qaLabel: "promo-title",
            marginBottom: 8
          },
          {
            text: selectedPromo.name,
            lowerText:
              get(selectedPromo, "TCLink", "") && registrationAllPromosWithTC
                ? "(TERMS APPLY)"
                : "",
            hasPromoIcon: true,
            qaLabel: "promo-text",
            marginBottom: 12
          }
        ]
      });
    } else if (hasRaf) {
      array.push({
        hasBorderBottom: false,
        wrapperOnClick: registrationAllPromosWithTC
          ? () => {
              onEditClickGtmEvent("Promo", "Review Your Information");
              handleEditHasPromo({ dispatch })();
            }
          : () => {},
        rows: [
          {
            title: "Referral Code",
            hasEdit: registrationAllPromosWithTC,
            handleEdit: () => {
              onEditClickGtmEvent("Promo", "Review Your Information");
              handleEditHasPromo({ dispatch })();
            },
            qaLabel: "raf-title",
            marginBottom: 8
          },
          {
            text: selectedPromo.name,
            lowerText: registrationAllPromosWithTC ? "(TERMS APPLY)" : "",
            hasRafIcon: true,
            qaLabel: "raf-text",
            marginBottom: 12
          }
        ]
      });
    }

    return array;
  };

export const ReviewComponent = ({ device }) => {
  const location = useLocation();
  const dispatch = useDispatch();

  const registrationTexts = useSelector(getRegistrationTexts);
  const robotMessage = useSelector(getRobotMessageToggle);
  const registrationAllPromosWithTC = useSelector(
    getRegistrationAllPromosWithTC
  );

  const enableRegistrationPerimeterX = useSelector(getPerimeterXToggle);
  const enableOnfido = useSelector(getOnfidoToggle);

  const currentStep = useSelector(getCurrentStep);
  const isEditing = useSelector(getIsEditing);
  const selectedPromo = useSelector(getSelectedPromo);
  const welcomeFields = useSelector(getWelcomeFields);
  const addressFields = useSelector(getAddressFields);
  const ssnFields = useSelector(getSsnFields);
  const selectedTvProvider = useSelector(getStoredSelectedProvider);

  const [isHumanChallengeEnabled, setIsHumanChallengeEnabled] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const onSubmit = handleSubmitRegistrationButton({
    registrationTexts,
    welcomeFields,
    addressFields,
    ssnFields,
    setIsLoading,
    selectedPromo,
    selectedTvProvider,
    setIsHumanChallengeEnabled,
    enableRegistrationPerimeterX,
    enableOnfido,
    dispatch
  });

  useEffect(() => {
    onVirtualPageViewGtmEvent(location, "review-details");
  }, []);

  useEffect(() => {
    const handler = async () => {
      setIsHumanChallengeEnabled(false);
      await onSubmit();
    };

    if (typeof window !== "undefined") {
      window.addEventListener("passedHC", handler);
    }

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("passedHC", handler);
      }
    };
  }, [onSubmit]);

  const hasPromo =
    selectedPromo && !!selectedPromo.code && selectedPromo.code !== "raf";
  const hasRaf =
    selectedPromo && !!selectedPromo.code && selectedPromo.code === "raf";

  const getRows = useCallback(
    buildBodyReview({
      dispatch,
      welcomeFields,
      addressFields,
      ssnFields,
      isPasswordVisible,
      setIsPasswordVisible,
      hasPromo,
      hasRaf,
      selectedPromo,
      selectedTvProvider,
      registrationTexts,
      registrationAllPromosWithTC
    }),
    [
      JSON.stringify(welcomeFields),
      JSON.stringify(addressFields),
      JSON.stringify(ssnFields),
      JSON.stringify(selectedTvProvider),
      JSON.stringify(selectedPromo),
      isPasswordVisible,
      hasPromo,
      hasRaf,
      registrationAllPromosWithTC
    ]
  );

  useEffect(() => {
    if (isEditing) {
      dispatch(setIsEditingRegistrationAction(false));
    }
  }, []);

  return (
    <Fragment>
      <ReviewPageRegistration
        device={device}
        brand={tvgConf().brand}
        innerTitle={get(registrationTexts, "review.innerTitle", "")}
        infoText={get(registrationTexts, "review.innerDescription", "")}
        handleBackButton={() => {
          onBackButtonClickGtmEvent(registrationTexts.review.innerTitle);
          handleBackButtonHeader({ dispatch, currentStep, device });
        }}
        handleLegalContentButton={handleLegalContentButton(dispatch, location)}
        setLegalContentOrigin={setLegalContentOrigin(dispatch)}
        currentStep={currentStep}
        groups={getRows()}
        handleSubmitRegistrationButton={onSubmit}
        isLoading={isLoading}
        passwordText={registrationTexts.review.passwordText}
        gamblingProblem={registrationTexts.review.gamblingProblem}
        legalContent={registrationTexts.review.legalContent}
        stateNotes={get(registrationTexts, "review.stateNotes")}
        userHomeState={get(addressFields, "home.state")}
        isHumanChallengeEnabled={isHumanChallengeEnabled}
        robotMessage={robotMessage}
      />
    </Fragment>
  );
};

ReviewComponent.defaultProps = {
  device: "desktop"
};

export default ReviewComponent;
